import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import nextId from 'react-id-generator';
import { OrderSelectBaseLayout } from '@component/OrderSelectBaseLayout';
import { OrderPartsHelpUnit } from '@component/OrderPartsHelp';
import { OrderPartsImg } from '@app/react/uikit/OrderPartsImg';
import { OnSelectProductHandler, ProductThumbnail } from '@app/react/uikit/ProductThumbnail';
import { IinitState } from '@app/react/reducer';
import type { AppState } from '@app/react/reducer';
import { hideEcHeaderNavi } from '@root/app/react/common/Function';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { GetResponseOrderParts, PartsProduct } from '@app/react/const/type';
import {
    apiGetActions,
    partsSelectActions,
} from '@app/react/action';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { useWindowScrollPosition } from '@app/react/hooks/useWindowScrollPosition';
import { selectOrderPartsHelp } from './selectors';

const nameRender: FC<PartsProduct> = ({
    name,
    price,
}) => (
    <>
        {`${name} (${price.toLocaleString()}円/本)`}
    </>
);

const priceRender: FC<PartsProduct> = ({ price, options: { unit = 0 } }) => (
    <>
        {`(¥${(price * unit).toLocaleString()})`}
    </>
);

export const OrderHonsuSelectContainer: FC<{ waitFetch?: boolean }> = ({ waitFetch = false }) => {
    const dispatch = useDispatch();
    const { arrowType, orderType } = useOrderPathParams();
    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();
    const htmlId = useMemo(() => nextId('order-parts-unit-products-'), []);
    const scrollObj = useRef(null);
    useWindowScrollPosition('scroll_unit', true, scrollObj);

    const customizeData = useSelector<AppState, IinitState>(
        (state) => state.customize,
    );
    const unitProductList = useSelector<AppState, GetResponseOrderParts | null>(
        (state) => state.APIData.unit,
    );
    const partsData = unitProductList?.partsData || [];

    const openHelpParts = useSelector(selectOrderPartsHelp);
    const isOpenHelp = openHelpParts === 'unit';

    const onSelectProduct: OnSelectProductHandler = useCallback(
        (__, product) => {
            dispatch(partsSelectActions.unit.started({ param: product }));
        },
        [dispatch],
    );

    const openHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: 'unit' }));
    }, [dispatch]);

    const closeHelp = useCallback(() => {
        dispatch(showOrderPartsHelpAction({ partsName: null }));
    }, [dispatch]);

    useEffect(() => {
        hideEcHeaderNavi(isDesktop, isMobile, dispatch);
    }, [isDesktop, isMobile, dispatch]);

    useEffect(() => {
        if (waitFetch) {
            console.log('initializing, waiting fetch');
            return;
        }
        dispatch(
            apiGetActions.unit.started({
                arrowType,
                orderType,
                selectItems: customizeData,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, arrowType, orderType, waitFetch]);

    return (
        <>
            <OrderSelectBaseLayout
                classNames={{ root: 'order-unit-select' }}
                header={isDesktop && (
                    <OrderPartsImg
                        partsName="矢の本数"
                        showHelpBtn
                        onHelpClick={openHelp}
                    />
                )}
                body={(
                    <div className="design-container">
                        <div className="container-overflow" ref={scrollObj}>
                            <p className={classNames('section-header', { hidden: isDesktop })}>
                                矢の本数
                            </p>
                            <ul className="product-container">
                                {partsData.map((product) => {
                                    const checked = customizeData.unit?.id === product.id;
                                    return (
                                        <ProductThumbnail
                                            key={product.id}
                                            product={product}
                                            onClick={onSelectProduct}
                                            checked={checked}
                                            groupName={htmlId}
                                            onNameRender={nameRender}
                                            onPriceRender={priceRender}
                                        />
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            />
            <OrderPartsHelpUnit
                isOpen={isOpenHelp}
                handleOpen={openHelp}
                handleClose={closeHelp}
            />
        </>
    );
};
